import { FC, useState, Dispatch, SetStateAction } from "react";
import { P2 } from "../atoms/Paragraph";
import styled from "styled-components";
import { XIcon, FacebookIcon, LinkedInIcon } from "../organisms/Footer/socialIcons";
import { ContentType } from "./BlogArticle";

export const BlogShareIcon: FC<{
  title: string;
  slug?: string;
  textVersion?: boolean;
  contentType?: ContentType;
}> = ({ title, slug, textVersion = false, contentType = "article" }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div style={{ position: "relative" }}>
      <div
        style={{ cursor: "pointer" }}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
      >
        {textVersion ? (
          <ShareSection>
            <P2 color="#A0A09B" style={{ marginRight: "1.5rem" }}>
              Share this {contentType === "article" ? "article" : "page"}
            </P2>
            <div style={{ transform: "translateY(0.66rem)" }}>
              <Icon />
            </div>
          </ShareSection>
        ) : (
          <div style={{ transform: "translateY(0.66rem)" }}>
            <Icon />
          </div>
        )}
      </div>
      {isOpen && (
        <SocialIconMenu
          title={title}
          slug={slug}
          setMenuOpen={setIsOpen}
          textVersion={textVersion}
          contentType={contentType}
        />
      )}
    </div>
  );
};

const SocialIconMenu: FC<
  React.PropsWithChildren<{
    title: string;
    slug?: string;
    textVersion?: boolean;
    setMenuOpen: Dispatch<SetStateAction<boolean>>;
    contentType?: ContentType;
  }>
> = ({ title, slug, setMenuOpen, textVersion = false, contentType }) => {
  const baseUrl = "https://yulife.com/";
  const sectionName = contentType === "vacancy" ? "careers" : "blog";

  const shareOnTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(
        `${baseUrl + sectionName}/${slug}`,
      )}&`,
      "share-twitter",
      "width=550,height=235",
    );
    setMenuOpen(false);
  };

  const shareOnFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer.php?u=${encodeURIComponent(
        `${baseUrl + sectionName}/${slug}`,
      )}`,
      "share-facebook",
      "width=580,height=582",
    );
    setMenuOpen(false);
  };

  const shareOnLinkedIn = () => {
    window.open(
      `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
        `${baseUrl + sectionName}/${slug}`,
      )}&title=${encodeURIComponent(title)}`,
      "share-linkedin",
      "width=580,height=582",
    );
    setMenuOpen(false);
  };

  return (
    <IconMenu textVersion={textVersion} contentType={contentType}>
      <ul
        style={{
          display: "flex",
        }}
      >
        <li onClick={shareOnTwitter}>
          <XIcon />
        </li>
        <li onClick={shareOnFacebook}>
          <FacebookIcon />
        </li>
        <li onClick={shareOnLinkedIn}>
          <LinkedInIcon />
        </li>
      </ul>
    </IconMenu>
  );
};

export const StandaloneSocialIcons: FC<
  React.PropsWithChildren<{
    title: string;
    slug?: string;
    textVersion?: boolean;
    contentType?: ContentType;
  }>
> = ({ title, slug, textVersion = false, contentType }) => {
  const baseUrl = "https://yulife.com/";
  const encodedUrl = encodeURIComponent(baseUrl + slug);
  const encodedTitle = encodeURIComponent(title);

  const shareOnTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}&`,
      "share-twitter",
      "width=550,height=235",
    );
  };

  const shareOnFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer.php?u=${encodedUrl}`,
      "share-facebook",
      "width=580,height=582",
    );
  };

  const shareOnLinkedIn = () => {
    window.open(
      `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}`,
      "share-linkedin",
      "width=580,height=582",
    );
  };

  return (
    <StandaloneIcons>
      <ul
        style={{
          display: "flex",
        }}
      >
        <li onClick={shareOnTwitter}>
          <XIcon />
        </li>
        <li onClick={shareOnFacebook}>
          <FacebookIcon />
        </li>
        <li onClick={shareOnLinkedIn}>
          <LinkedInIcon />
        </li>
      </ul>
    </StandaloneIcons>
  );
};

const StandaloneIcons = styled.div`
  padding-top: 2rem;
  padding-bottom: 2rem;
`;

const IconMenu = styled.div<{ textVersion?: boolean; contentType?: ContentType }>`
  position: absolute;
  left: ${(p) => (p.textVersion ? (p.contentType === "article" ? "6.1rem" : "5.3rem") : "-4.2rem")};
  top: ${(p) => (p.textVersion ? "3.5rem" : "2.8rem")};
  width: fit-content;
  border: 0.125rem solid #e3e3e1;
  box-shadow: 0.25rem 0.25rem 0 #f5f5f5;
  border-radius: 0.5rem;
  padding: 0 0.25rem;
  background: #fff;
  z-index: 9;

  &:before {
    content: "";
    display: block;
    width: 0;
    height: 0;
    position: absolute;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 10px solid #e3e3e1;
    top: -10px;
    left: 4.15rem;
  }

  animation: fadein 250ms;

  @keyframes fadein {
    0% {
      opacity: 0;
      transform: scale(0);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const ShareSection = styled.div`
  display: inline-flex;
  align-items: center;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const Icon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none">
    <path
      d="M18 8a3 3 0 100-6 3 3 0 000 6zM6 15a3 3 0 100-6 3 3 0 000 6zM18 22a3 3 0 100-6 3 3 0 000 6zM8.6 13.5l6.8 4M15.4 6.5l-6.8 4"
      stroke="#A0A09B"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
